/**
 * Localize the enum-valued `details` params the backend spreads into error copy.
 *
 * A refusal like `SALES_DELIVERY_NOT_SHIPPABLE` ships
 * `details={"status": "planned"}` — the raw column value. `getErrorMessage`
 * spreads that map into `i18n.t()`, so a message reading
 * "Only a packed delivery can be shipped — this one is {{status}}." rendered
 * the token `planned` in Spanish, Hebrew and Chinese alike.
 *
 * The localized labels already exist: every `StatusChip` in the app renders the
 * same value through an i18n label group (`sales:deliveries.status.planned` =
 * Planificado / מתוכנן / 已计划). What the seam lacks is the link from an error
 * CODE to the group its status belongs to — `details` is a bare dict, and the
 * code prefix does not identify the enum (`SALES_DELIVERY_ORDER_NOT_SHIPPABLE`
 * carries a sales-ORDER status, not a delivery one). So that link is declared
 * here, once, and is the only thing this module adds: the labels themselves stay
 * single-sourced in the locale bundles.
 *
 * Adding an errors.json message that interpolates `{{status}}` or `{{*_status}}`
 * without a matching entry below fails
 * `__tests__/errorStatusLabels.test.ts` — see `isStatusParam`.
 */

import i18n from '../../i18n/config';

/**
 * A `details` param whose value is an enum token needing a localized label.
 *
 * Matched by NAME rather than an enumerated list so a new `{{..._status}}`
 * placeholder is policed the moment it lands, instead of silently escaping a
 * list nobody remembered to extend.
 *
 * Deliberately narrow. Other `details` params carrying a code-shaped value —
 * `{{reason_code}}` (a tenant-configured receiving reason), `{{basis}}`,
 * `{{mode}}`, `{{rate_type}}`, `{{line_type}}`, `{{incoterm_code}}`,
 * `{{code}}`, `{{invalid}}`, `{{values}}` — are all messages echoing back the
 * INVALID value the caller just sent ("'{{mode}}' is not a valid restocking-fee
 * type"). There is no label to resolve for a value that is not in the enum, and
 * echoing it verbatim is the point of the copy.
 */
export function isStatusParam(name: string): boolean {
  return name === 'status' || name.endsWith('_status');
}

/**
 * Error code → (`details` param → i18n label-group prefix).
 *
 * The prefix is namespace-qualified and points at the SAME group the entity's
 * status chip renders, so the toast and the chip can never disagree. A value
 * with no label under the prefix falls back to the raw value — which is both
 * the old behaviour and the right answer for a message whose whole subject is a
 * value that is not in the enum (`LICENSE_PLATE_INVALID_STATUS`).
 */
export const ERROR_STATUS_LABEL_KEYS: Record<string, Record<string, string>> = {
  // ---- Sales: delivery status (sales_deliveries.status) --------------------
  // modules/sales/services/delivery_service.py
  SALES_DELIVERY_NOT_RELEASABLE: { status: 'sales:deliveries.status' },
  SALES_DELIVERY_NOT_REPLANNABLE: { status: 'sales:deliveries.status' },
  SALES_DELIVERY_NOT_PICKABLE: { status: 'sales:deliveries.status' },
  SALES_DELIVERY_NOT_PACKABLE: { status: 'sales:deliveries.status' },
  SALES_DELIVERY_NOT_SHIPPABLE: { status: 'sales:deliveries.status' },
  SALES_DELIVERY_DROP_SHIP_NOT_SHIPPABLE: { status: 'sales:deliveries.status' },
  SALES_DELIVERY_NOT_DELIVERABLE: { status: 'sales:deliveries.status' },
  SALES_DELIVERY_NOT_CANCELLABLE: { status: 'sales:deliveries.status' },
  SALES_DELIVERY_NOT_PICKED: { status: 'sales:deliveries.status' },

  // ---- Sales: order status (sales_orders.status) ---------------------------
  // Note the two SALES_DELIVERY_* / SALES_INBOUND_* codes here: the refusal is
  // raised on a delivery/allocation path but the status it reports belongs to
  // the parent ORDER, which is exactly why this map is per-code and not derived
  // from the code prefix.
  // modules/sales/services/delivery_service.py
  SALES_DELIVERY_ORDER_NOT_SHIPPABLE: { status: 'sales:status' },
  // modules/sales/services/order_service.py
  SALES_ORDER_INITIAL_STATUS_INVALID: { status: 'sales:status' },
  SALES_ORDER_NOT_EDITABLE: { status: 'sales:status' },
  SALES_ORDER_NOT_CONFIRMABLE: { status: 'sales:status' },
  SALES_ORDER_NOT_HOLDABLE: { status: 'sales:status' },
  SALES_ORDER_NOT_RELEASABLE: { status: 'sales:status' },
  SALES_ORDER_NOT_CANCELLABLE: { status: 'sales:status' },
  SALES_ORDER_NOT_CLOSABLE: { status: 'sales:status' },
  SALES_ORDER_NOT_CLOSABLE_UNCONFIRMED: { status: 'sales:status' },
  SALES_ORDER_NOT_ALLOCATABLE: { status: 'sales:status' },
  SALES_ORDER_FROM_DEAL_STATUS_INVALID: { status: 'sales:status' },
  SALES_QUOTE_NOT_REVISABLE: { status: 'sales:status' },
  SALES_QUOTE_NOT_CONVERTIBLE: { status: 'sales:status' },
  // modules/sales/services/inbound_allocation_service.py
  SALES_INBOUND_ORDER_NOT_CONFIRMED: { status: 'sales:status' },
  // modules/sales/services/special_order_service.py
  SALES_SPECIAL_ORDER_ORDER_NOT_GENERATABLE: { status: 'sales:status' },
  SALES_SPECIAL_ORDER_ORDER_NOT_LINKABLE: { status: 'sales:status' },
  // modules/sales/services/drop_ship_service.py
  SALES_DROP_SHIP_ORDER_NOT_GENERATABLE: { status: 'sales:status' },
  SALES_DROP_SHIP_ORDER_NOT_LINKABLE: { status: 'sales:status' },
  SALES_DROP_SHIP_RECEIPT_ORDER_NOT_CONFIRMED: { status: 'sales:status' },

  // ---- Sales: customer-return status (sales_returns.status) ----------------
  // modules/sales/services/return_service.py
  SALES_RETURN_NOT_EDITABLE: { status: 'sales:returns.status' },
  SALES_RETURN_ONLY_FEE_EDITABLE: { status: 'sales:returns.status' },
  SALES_RETURN_NOT_AUTHORISABLE: { status: 'sales:returns.status' },
  SALES_RETURN_NOT_RECEIVABLE: { status: 'sales:returns.status' },
  SALES_RETURN_NOT_CLOSABLE: { status: 'sales:returns.status' },
  SALES_RETURN_NOT_CANCELLABLE: { status: 'sales:returns.status' },
  SALES_RETURN_NOT_DELETABLE: { status: 'sales:returns.status' },

  // ---- Sales: commission payout run status (sales_commission_payout_runs.status) --
  // modules/sales/services/commission_payout_service.py. The chip on
  // CommissionPayoutsListPage reads this same group.
  SALES_COMMISSION_PAYOUT_RUN_NOT_DRAFT: { status: 'sales:commissionPayouts.status' },

  // ---- Procurement: goods-receipt status (proc_goods_receipts.status) ------
  // modules/procurement/services/goods_receipt_service.py
  GOODS_RECEIPT_NOT_DRAFT: { status: 'procurement:goodsReceipts.statuses' },
  // modules/sales/services/inbound_allocation_service.py — sales code, receipt status
  SALES_INBOUND_RECEIPT_NOT_RECEIVED: { status: 'procurement:goodsReceipts.statuses' },

  // ---- Procurement: inbound-delivery status --------------------------------
  // modules/procurement/services/inbound_delivery_service.py. The chip is
  // InboundDeliveryStatusChip, which reads this same group. INVALID_STATUS
  // echoes back a value outside the enum and so resolves to the raw echo, same
  // as LICENSE_PLATE_INVALID_STATUS below.
  INBOUND_DELIVERY_INVALID_STATUS: { status: 'procurement:purchaseOrders.inboundDeliveryStatus' },
  INBOUND_DELIVERY_NOT_REPLANNABLE: { status: 'procurement:purchaseOrders.inboundDeliveryStatus' },

  // ---- Procurement: license-plate status (proc_license_plates.status) ------
  // modules/procurement/services/license_plate_service.py. The value here is by
  // definition NOT in the enum, so it resolves to the raw echo — the entry is
  // declared anyway so the policing test covers the code rather than needing an
  // opt-out, and so a valid-but-rejected value would still localize.
  LICENSE_PLATE_INVALID_STATUS: { status: 'procurement:goodsReceipts.licensePlates.statuses' },

  // ---- Procurement: vendor-return status (proc_vendor_returns.status) ------
  // modules/procurement/services/vendor_return_service.py
  VENDOR_RETURN_NOT_EDITABLE: { status: 'procurement:vendorReturns.status' },
  VENDOR_RETURN_NOT_AUTHORISABLE: { status: 'procurement:vendorReturns.status' },
  VENDOR_RETURN_NOT_SHIPPABLE: { status: 'procurement:vendorReturns.status' },
  VENDOR_RETURN_NOT_CLOSABLE: { status: 'procurement:vendorReturns.status' },
  VENDOR_RETURN_NOT_CANCELLABLE: { status: 'procurement:vendorReturns.status' },
  VENDOR_RETURN_NOT_DELETABLE: { status: 'procurement:vendorReturns.status' },
  VENDOR_RETURN_CREDIT_INVALID_STATUS: { status: 'procurement:vendorReturns.status' },

  // ---- Procurement: purchase-order status, as {{po_status}} ----------------
  // The second enum-valued param in error copy, found by the audit this work
  // item asked for. modules/sales/services/{special_order,drop_ship}_service.py
  SALES_SPECIAL_ORDER_PO_NOT_LINKABLE: { po_status: 'procurement:purchaseOrders.statuses' },
  SALES_DROP_SHIP_PO_NOT_LINKABLE: { po_status: 'procurement:purchaseOrders.statuses' },
  SALES_DROP_SHIP_RECEIPT_PO_STATUS_BLOCKED: { po_status: 'procurement:purchaseOrders.statuses' },
};

/**
 * Return `params` with every declared enum param replaced by its localized
 * label, in the viewer's current language.
 *
 * Returns the input untouched when there is nothing to do, so the common case
 * (most codes declare nothing) allocates nothing. Non-string values are left
 * alone: `SALES_INBOUND_ORDER_NOT_CONFIRMED` can send `status: null` when the
 * parent order is missing entirely, and a null has no label to look up.
 */
export function localizeErrorStatusParams<T>(
  code: string | undefined,
  params: T,
): T {
  const labelKeys = code ? ERROR_STATUS_LABEL_KEYS[code] : undefined;
  if (!labelKeys || !params || typeof params !== 'object') return params;

  let localized: Record<string, unknown> | null = null;
  for (const [param, prefix] of Object.entries(labelKeys)) {
    const value = (params as Record<string, unknown>)[param];
    if (typeof value !== 'string' || !value) continue;
    // `defaultValue` keeps the raw token for a value outside the enum — an
    // invalid value the message is echoing back, or a status added to the DB
    // CHECK before its label landed.
    const label = i18n.t(`${prefix}.${value}`, { defaultValue: value });
    if (label === value) continue;
    localized ??= { ...(params as Record<string, unknown>) };
    localized[param] = label;
  }

  return (localized ?? params) as T;
}
